<template>
  <div class="st-config">
    <!-- Connection Section -->
    <div class="st-section">
      <div class="st-section-header" @click="sections.connection = !sections.connection">
        <div class="st-section-header-left">
          <UpOutlined v-if="!sections.connection" class="cursor-pointer" />
          <DownOutlined v-else class="cursor-pointer" />
          <span class="st-section-title">Connection</span>
        </div>
        <a-tag v-if="tower_host" color="green" class="st-status-tag">
          {{ tower_host }}
        </a-tag>
      </div>
      <div v-if="sections.connection" class="st-section-body">
        <div class="st-field-row">
          <span class="st-label">Tower Host IP</span>
          <div class="st-field-control">
            <a-input
              v-model:value="tower_host"
              :disabled="useDefaultIp"
              placeholder="192.168.10.1"
              style="width: 180px"
              size="small"
            />
            <a-checkbox v-model:checked="useDefaultIp" class="st-checkbox">
              Default
            </a-checkbox>
            <a-button
              size="small"
              type="primary"
              :disabled="useDefaultIp"
              :loading="savingHost"
              @click="updateTowerHost"
            >
              Update
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Buzzer Volume Section -->
    <div class="st-section">
      <div class="st-section-header" @click="sections.buzzer = !sections.buzzer">
        <div class="st-section-header-left">
          <UpOutlined v-if="!sections.buzzer" class="cursor-pointer" />
          <DownOutlined v-else class="cursor-pointer" />
          <span class="st-section-title">Buzzer Volume</span>
        </div>
        <a-tag class="st-status-tag">
          {{ buzzerVolumeLabel }}
        </a-tag>
      </div>
      <div v-if="sections.buzzer" class="st-section-body">
        <div class="st-volume-control">
          <div
            v-for="option in buzzerVolumeOptions"
            :key="option.value"
            class="st-volume-option"
            :class="{ 'st-volume-active': buzzerVolume === option.value }"
            @click="buzzerVolume = option.value"
          >
            <div class="st-volume-icon">
              <SoundOutlined v-if="option.bars > 0" />
              <span v-else style="font-size: 14px">&#8416;</span>
            </div>
            <span class="st-volume-label">{{ option.label }}</span>
            <div
              v-if="option.bars > 0"
              class="st-volume-bars"
            >
              <div
                v-for="bar in 2"
                :key="bar"
                class="st-volume-bar"
                :class="{
                  'st-volume-bar-filled': bar <= option.bars,
                  'st-volume-bar-active': buzzerVolume === option.value && bar <= option.bars
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Signal States Section -->
    <div class="st-section">
      <div class="st-section-header" @click="sections.signals = !sections.signals">
        <div class="st-section-header-left">
          <UpOutlined v-if="!sections.signals" class="cursor-pointer" />
          <DownOutlined v-else class="cursor-pointer" />
          <span class="st-section-title">Signal States</span>
        </div>
        <span class="st-section-subtitle">{{ enabledSignalCount }} of {{ signalKeys.length }} active</span>
      </div>
      <div v-if="sections.signals" class="st-section-body st-signals-body">
        <div
          v-for="key in signalKeys"
          :key="key"
          class="st-signal-card"
          :class="{ 'st-signal-card-disabled': !signal_tower[key].enable }"
        >
          <div class="st-signal-card-header">
            <div class="st-signal-card-left">
              <div
                class="st-color-dot"
                :style="{ backgroundColor: signal_tower[key].enable ? colors[signal_tower[key].color]?.hex : '#d9d9d9' }"
              />
              <span class="st-signal-name">{{ labels[key] }}</span>
            </div>
            <a-switch
              v-model:checked="signal_tower[key].enable"
              size="small"
            />
          </div>

          <div v-if="signal_tower[key].enable" class="st-signal-card-body">
            <div class="st-signal-control">
              <span class="st-signal-control-label">Buzzer</span>
              <a-switch
                v-model:checked="signal_tower[key].buzzer"
                size="small"
              />
            </div>
            <div class="st-signal-control">
              <span class="st-signal-control-label">Color</span>
              <a-dropdown :trigger="['click']">
                <div class="st-color-picker-trigger cursor-pointer">
                  <div
                    class="st-color-preview"
                    :style="{ backgroundColor: colors[signal_tower[key].color]?.hex }"
                  />
                  <span class="st-color-name">{{ colors[signal_tower[key].color]?.name }}</span>
                  <DownOutlined style="font-size: 10px; color: #a0a0a0" />
                </div>
                <template #overlay>
                  <a-menu>
                    <a-menu-item
                      v-for="(colorObj, colorKey) in colors"
                      :key="colorKey"
                      @click="signal_tower[key].color = colorKey"
                    >
                      <div class="st-color-menu-item">
                        <div
                          class="st-color-swatch"
                          :style="{ backgroundColor: colorObj.hex }"
                        />
                        <span>{{ colorObj.name }}</span>
                      </div>
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  UpOutlined,
  DownOutlined,
  SoundOutlined,
} from '@ant-design/icons-vue';
import DeviceService from 'src/services/device';
import httpClient from 'src/service/httpClient';

export default {
  name: 'SignalTowerConfig',
  components: {
    UpOutlined,
    DownOutlined,
    SoundOutlined,
  },
  props: ['device'],
  inject: ['toast'],
  data() {
    return {
      sections: {
        connection: true,
        buzzer: true,
        signals: true,
      },
      colors: {
        11111: { name: 'Red', hex: '#ff0000' },
        22222: { name: 'Amber', hex: '#FFBF00' },
        33333: { name: 'Lemon', hex: '#FAFA33' },
        44444: { name: 'Green', hex: '#98FB98' },
        55555: { name: 'Sky Blue', hex: '#00B5E2' },
        66666: { name: 'Blue', hex: '#0000FF' },
        77777: { name: 'Purple', hex: '#800080' },
        88888: { name: 'Pink', hex: '#F33A6A' },
        99999: { name: 'White', hex: '#FFFFFF' },
      },
      signal_tower: {
        start: { enable: true, color: '44444', buzzer: false },
        missing: { enable: false, color: '11111', buzzer: false },
        clear_missing: { enable: false, color: '44444', buzzer: false },
        end: { enable: false, color: '66666', buzzer: false },
        idle: { enable: false, color: '66666', buzzer: false },
      },
      labels: {
        start: 'Cycle Start',
        missing: 'Step Miss',
        clear_missing: 'Miss Cleared',
        end: 'Cycle End',
        idle: 'Idle',
      },
      signalKeys: ['start', 'missing', 'clear_missing', 'end', 'idle'],
      organization: localStorage.getItem('organization'),
      useDefaultIp: true,
      tower_host: '192.168.10.1',
      selectedDevice: {},
      buzzerVolume: 1,
      buzzerVolumeOptions: [
        { label: 'Max', value: 1, bars: 2 },
        { label: 'Low', value: 5, bars: 1 },
        { label: 'Mute', value: 0, bars: 0 },
      ],
      previewLabels: {
        start: 'Start',
        missing: 'Miss',
        clear_missing: 'Cleared',
        end: 'End',
        idle: 'Idle',
      },
      savingHost: false,
      savingConfig: false,
    };
  },
  computed: {
    buzzerVolumeLabel() {
      const option = this.buzzerVolumeOptions.find(o => o.value === this.buzzerVolume);
      return option?.label || 'Max';
    },
    enabledSignalCount() {
      return this.signalKeys.filter(key => this.signal_tower[key].enable).length;
    },
    towerPreviewLights() {
      return this.signalKeys.map(key => {
        const bgColor = this.signal_tower[key].enable
          ? this.colors[this.signal_tower[key].color]?.hex
          : '#e8e8e8';
        return {
          key,
          label: this.previewLabels[key],
          color: bgColor,
          textColor: this.getContrastColor(bgColor),
        };
      });
    },
  },
  async created() {
    await this.configureSignalTower();
  },
  methods: {
    getContrastColor(hex) {
      if (!hex) return '#000';
      const c = hex.replace('#', '');
      const r = parseInt(c.substr(0, 2), 16);
      const g = parseInt(c.substr(2, 2), 16);
      const b = parseInt(c.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.85)';
    },
    async configureSignalTower() {
      const order = ['start', 'missing', 'clear_missing', 'end', 'idle'];
      let sortedData = {};
      const deviceSettingsURL =
        'device/' + this.organization + '/device_settings/' + this.device + '/';
      const list = await httpClient.get(deviceSettingsURL, false);
      const [deviceSetting] = list;
      this.selectedDevice = deviceSetting;

      if (deviceSetting['tower_host']) {
        this.tower_host = deviceSetting['tower_host'];
      }

      if (Object.keys(deviceSetting['tower_settings']).length > 0) {
        const settings = deviceSetting['tower_settings'];
        if (settings.buzzer_volume !== undefined) {
          this.buzzerVolume = settings.buzzer_volume;
        }
        order.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(settings, key)) {
            sortedData[key] = settings[key];
          }
        });
        this.signal_tower = sortedData;
      }

      Object.keys(this.signal_tower).forEach((key) => {
        this.signal_tower[key].enable = Boolean(this.signal_tower[key].enable);
        this.signal_tower[key].buzzer = Boolean(this.signal_tower[key].buzzer);
      });
    },
    isValidIPv4(ip) {
      const pattern =
        /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      return pattern.test(ip);
    },
    transformSignalTowerSetting() {
      const transformedSetting = JSON.parse(JSON.stringify(this.signal_tower));
      Object.keys(transformedSetting).forEach((key) => {
        transformedSetting[key].enable = Number(transformedSetting[key].enable);
        transformedSetting[key].buzzer = Number(transformedSetting[key].buzzer);
      });
      return {
        buzzer_volume: this.buzzerVolume,
        ...transformedSetting,
        reset: { clear: 1, enable: 1 },
      };
    },
    async updateTowerHost() {
      if (!this.isValidIPv4(this.tower_host)) {
        return this.toast.error('Invalid tower host IP', { timeout: 3000 });
      }
      this.savingHost = true;
      const [error] = await DeviceService.updateDeviceSetting(
        this.selectedDevice?.id,
        this.organization,
        { tower_host: this.tower_host }
      );
      this.savingHost = false;
      if (error) {
        return this.toast.error('Error updating tower host', { timeout: 3000 });
      }
      this.toast.success(
        'Tower host updated. Restart software to sync tower.',
        { timeout: 5000 }
      );
      this.useDefaultIp = true;
    },
    async saveSignalConfig() {
      this.savingConfig = true;
      const payload = {
        tower_settings: this.transformSignalTowerSetting(),
      };
      const [error] = await DeviceService.updateDeviceSetting(
        this.selectedDevice?.id,
        this.organization,
        payload
      );
      this.savingConfig = false;
      if (error) {
        return this.toast.error('Error saving signal tower configuration', {
          timeout: 3000,
        });
      }
      return this.toast.success('Signal tower configuration saved', {
        timeout: 3000,
      });
    },
  },
};
</script>

<style scoped>
.st-config {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  overflow: hidden;
}

/* Sections */
.st-section {
  border-bottom: 1px solid #e8e8e8;
}
.st-section:last-of-type {
  border-bottom: none;
}

.st-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #f6f6f6;
  cursor: pointer;
  user-select: none;
}
.st-section-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.st-section-title {
  font-weight: 600;
  font-size: 14px;
  color: #343a40;
}
.st-section-subtitle {
  font-size: 12px;
  color: #808080;
}
.st-status-tag {
  margin: 0;
  font-size: 12px;
}

.st-section-body {
  padding: 16px;
  background-color: #fff;
}

/* Connection */
.st-field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.st-label {
  font-weight: 400;
  font-size: 14px;
  color: #343a40;
}
.st-field-control {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.st-checkbox {
  font-size: 13px;
}

/* Buzzer Volume */
.st-volume-control {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.st-volume-option {
  flex: 1;
  min-width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 8px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.st-volume-option:hover {
  border-color: #b3d4ff;
  background-color: #f0f7ff;
}
.st-volume-active {
  border-color: #047bf8;
  background-color: #f0f7ff;
}
.st-volume-icon {
  font-size: 20px;
  color: #636c72;
}
.st-volume-active .st-volume-icon {
  color: #047bf8;
}
.st-volume-label {
  font-size: 13px;
  font-weight: 500;
  color: #343a40;
}
.st-volume-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 16px;
}
.st-volume-bar {
  width: 6px;
  background-color: #e8e8e8;
  border-radius: 2px;
}
.st-volume-bar:nth-child(1) { height: 8px; }
.st-volume-bar:nth-child(2) { height: 16px; }
.st-volume-bar-filled {
  background-color: #d9d9d9;
}
.st-volume-bar-active {
  background-color: #047bf8;
}

/* Signal Cards */
.st-signals-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
}
.st-signal-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}
.st-signal-card-disabled {
  opacity: 0.65;
}
.st-signal-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: #fafafa;
}
.st-signal-card-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.st-color-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}
.st-signal-name {
  font-weight: 500;
  font-size: 14px;
  color: #343a40;
}
.st-signal-card-body {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 10px 14px 10px 38px;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;
}
.st-signal-control {
  display: flex;
  align-items: center;
  gap: 8px;
}
.st-signal-control-label {
  font-size: 13px;
  color: #636c72;
}

/* Color Picker Trigger */
.st-color-picker-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
}
.st-color-picker-trigger:hover {
  border-color: #047bf8;
}
.st-color-preview {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.st-color-name {
  font-size: 13px;
  color: #343a40;
}
.st-color-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.st-color-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .st-section-header {
    padding: 10px 12px;
  }
  .st-section-body {
    padding: 12px;
  }
  .st-field-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .st-volume-control {
    gap: 8px;
  }
  .st-volume-option {
    padding: 10px 6px;
    min-width: 60px;
  }
  .st-volume-icon {
    font-size: 16px;
  }
  .st-volume-label {
    font-size: 12px;
  }
  .st-signal-card-body {
    padding: 10px 14px;
    gap: 16px;
  }
  .st-signals-body {
    padding: 10px 12px;
  }
}

@media (max-width: 480px) {
  .st-volume-control {
    flex-direction: column;
  }
  .st-volume-option {
    flex-direction: row;
    justify-content: center;
    gap: 10px;
    padding: 10px 14px;
  }
  .st-field-control {
    width: 100%;
  }
}
</style>
